import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Stage, Layer, Rect, Circle, Text } from 'react-konva';
import { useGameAudio } from '../GameAudioProvider';
import { GAME_SCREENS } from '../constants';
import type { GameScreen } from '../constants';
import BackButton from '../BackButton';

interface Raindrop {
  id: number;
  x: number;
  y: number;
  speed: number;
}

interface Player {
  x: number;
  y: number;
  width: number;
  height: number;
  velocityY: number;
  isJumping: boolean;
}

const DodgeTheRainGame: React.FC = () => {
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [gameState, setGameState] = useState<'playing' | 'gameOver'>('playing');
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [player, setPlayer] = useState<Player>({
    x: 400,
    y: 500,
    width: 40,
    height: 40,
    velocityY: 0,
    isJumping: false
  });
  const [raindrops, setRaindrops] = useState<Raindrop[]>([]);
  const [keys, setKeys] = useState<Set<string>>(new Set());
  const [rainSpeed, setRainSpeed] = useState(1.0);
  
  const gameLoopRef = useRef<number | null>(null);
  const raindropIdRef = useRef(0);
  const lastSpawnRef = useRef(0);
  const hasStartedRef = useRef(false);
  const { stopAudio, startBackgroundMusic, kinzySpeak } = useGameAudio();

  const PLAYER_SPEED = 8;
  const JUMP_FORCE = -15;
  const GRAVITY = 0.8;
  const GROUND_Y = dimensions.height - 100;
  const RAINDROP_SIZE = 20;

  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      startBackgroundMusic();
      
      const speakInstructions = async () => {
        await kinzySpeak('Game starting! Use arrow keys or on-screen buttons to move. Press space or the up button to jump. Avoid the falling raindrops!');
      };
      speakInstructions();
      
      resetGame();
    }
  }, [startBackgroundMusic, kinzySpeak]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      setKeys(prev => new Set(prev).add(e.key));
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      setKeys(prev => {
        const newKeys = new Set(prev);
        newKeys.delete(e.key);
        return newKeys;
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const spawnRaindrop = useCallback(() => {
    const now = Date.now();
    const spawnRate = Math.max(300, 1000 - (rainSpeed - 1) * 500);
    
    if (now - lastSpawnRef.current > spawnRate) {
      lastSpawnRef.current = now;
      const newRaindrop: Raindrop = {
        id: raindropIdRef.current++,
        x: Math.random() * dimensions.width,
        y: -RAINDROP_SIZE,
        speed: 3 * rainSpeed
      };
      setRaindrops(prev => [...prev, newRaindrop]);
    }
  }, [rainSpeed, dimensions.width]);

  const checkCollision = useCallback((playerPos: Player, drop: Raindrop): boolean => {
    const playerCenterX = playerPos.x + playerPos.width / 2;
    const playerCenterY = playerPos.y + playerPos.height / 2;
    const dropCenterX = drop.x + RAINDROP_SIZE / 2;
    const dropCenterY = drop.y + RAINDROP_SIZE / 2;
    
    return (
      playerCenterX + playerPos.width / 2 > dropCenterX - RAINDROP_SIZE / 2 &&
      playerCenterX - playerPos.width / 2 < dropCenterX + RAINDROP_SIZE / 2 &&
      playerCenterY + playerPos.height / 2 > dropCenterY - RAINDROP_SIZE / 2 &&
      playerCenterY - playerPos.height / 2 < dropCenterY + RAINDROP_SIZE / 2
    );
  }, []);

  const gameLoop = useCallback(() => {
    if (gameState !== 'playing') return;

    setPlayer(prev => {
      let newX = prev.x;
      let newY = prev.y;
      let newVelocityY = prev.velocityY;
      let newIsJumping = prev.isJumping;

      if (keys.has('ArrowLeft') || keys.has('a')) {
        newX = Math.max(0, prev.x - PLAYER_SPEED);
      }
      if (keys.has('ArrowRight') || keys.has('d')) {
        newX = Math.min(dimensions.width - prev.width, prev.x + PLAYER_SPEED);
      }
      if ((keys.has(' ') || keys.has('ArrowUp') || keys.has('w')) && !prev.isJumping) {
        newVelocityY = JUMP_FORCE;
        newIsJumping = true;
      }

      newVelocityY += GRAVITY;
      newY += newVelocityY;

      if (newY >= GROUND_Y) {
        newY = GROUND_Y;
        newVelocityY = 0;
        newIsJumping = false;
      }

      return {
        ...prev,
        x: newX,
        y: newY,
        velocityY: newVelocityY,
        isJumping: newIsJumping
      };
    });

    setRaindrops(prev => {
      const updated = prev
        .map(drop => ({
          ...drop,
          y: drop.y + drop.speed
        }))
        .filter(drop => drop.y < dimensions.height);

      return updated;
    });

    spawnRaindrop();

    setRaindrops(currentDrops => {
      setPlayer(currentPlayer => {
        const collided = currentDrops.some(drop => checkCollision(currentPlayer, drop));
        
        if (collided) {
          stopAudio();
          setGameState('gameOver');
          setHighScore(prev => Math.max(prev, score));
          
          // Speak game over message
          const speakGameOver = async () => {
            await kinzySpeak(`Game over! Your final score is ${score}. ${score > highScore ? 'New high score!' : 'Try again to beat your high score!'}`);
          };
          speakGameOver();
          
          return currentPlayer;
        }
        return currentPlayer;
      });
      return currentDrops;
    });

    setScore(prev => prev + 1);
    setRainSpeed(prev => Math.min(3.0, 1.0 + Math.floor(score / 500) * 0.1));
  }, [gameState, keys, dimensions, spawnRaindrop, checkCollision, score, stopAudio]);

  useEffect(() => {
    if (gameState === 'playing') {
      gameLoopRef.current = window.setInterval(gameLoop, 1000 / 60);
    } else {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    }

    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [gameState, gameLoop]);

  const resetGame = () => {
    setGameState('playing');
    setScore(0);
    setRainSpeed(1.0);
    setPlayer({
      x: dimensions.width / 2 - 20,
      y: GROUND_Y,
      width: 40,
      height: 40,
      velocityY: 0,
      isJumping: false
    });
    setRaindrops([]);
    raindropIdRef.current = 0;
    lastSpawnRef.current = 0;
  };

  const handleMobileControl = (action: 'left' | 'right' | 'jump') => {
    if (action === 'jump' && !player.isJumping) {
      setPlayer(prev => ({
        ...prev,
        velocityY: JUMP_FORCE,
        isJumping: true
      }));
    } else if (action === 'left') {
      setPlayer(prev => ({
        ...prev,
        x: Math.max(0, prev.x - PLAYER_SPEED * 2)
      }));
    } else if (action === 'right') {
      setPlayer(prev => ({
        ...prev,
        x: Math.min(dimensions.width - prev.width, prev.x + PLAYER_SPEED * 2)
      }));
    }
  };

  const handleBack = () => {
    stopAudio();
    if (typeof window !== 'undefined' && window.flutterExit) {
      window.flutterExit();
    }
  };

  return (
    <div className="relative w-full h-screen overflow-hidden" style={{
      backgroundImage: 'url(https://kinzy.club/games/assets/space-defender-game/mars_background_image.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}>
      <BackButton
        currentScreen={GAME_SCREENS.GAMEPLAY}
        currentLevel={1}
        onBack={handleBack}
        position="top-left"
      />

      <Stage width={dimensions.width} height={dimensions.height}>
        <Layer>
          <Rect
            x={0}
            y={0}
            width={dimensions.width}
            height={dimensions.height}
            fill="rgba(0, 0, 0, 0.3)"
          />

          <Text
            x={player.x + player.width / 2}
            y={player.y + player.height / 2}
            text="🤠"
            fontSize={32}
            offsetX={16}
            offsetY={16}
          />

          <Rect
            x={player.x}
            y={player.y}
            width={player.width}
            height={player.height}
            fill="transparent"
            cornerRadius={5}
          />

          {raindrops.map(drop => (
            <Circle
              key={drop.id}
              x={drop.x + RAINDROP_SIZE / 2}
              y={drop.y + RAINDROP_SIZE / 2}
              radius={RAINDROP_SIZE / 2}
              fill="#2196F3"
            />
          ))}

          <Rect
            x={0}
            y={GROUND_Y + player.height}
            width={dimensions.width}
            height={dimensions.height - GROUND_Y - player.height}
            fill="#8B4513"
          />

          <Text
            x={80}
            y={20}
            text={`Score: ${score}`}
            fontSize={16}
            fill="white"
            fontStyle="bold"
          />

          <Text
            x={80}
            y={45}
            text={`Rain Speed: ${rainSpeed.toFixed(1)}x`}
            fontSize={16}
            fill="white"
            fontStyle="bold"
          />

          <Text
            x={80}
            y={70}
            text={`High Score: ${highScore}`}
            fontSize={16}
            fill="yellow"
            fontStyle="bold"
          />

          <Rect
            x={70}
            y={0}
            width={210}
            height={100}
            fill="rgba(0, 0, 0, 0.3)"
            cornerRadius={10}
          />

          {gameState === 'gameOver' && (
            <>
              <Rect
                x={dimensions.width / 2 - 200}
                y={dimensions.height / 2 - 100}
                width={400}
                height={200}
                fill="rgba(0, 0, 0, 0.8)"
                cornerRadius={10}
              />
              <Text
                x={dimensions.width / 2}
                y={dimensions.height / 2 - 50}
                text="Game Over!"
                fontSize={48}
                fill="red"
                fontStyle="bold"
                align="center"
                width={400}
                offsetX={200}
              />
              <Text
                x={dimensions.width / 2}
                y={dimensions.height / 2 + 10}
                text={`Final Score: ${score}`}
                fontSize={24}
                fill="white"
                align="center"
                width={400}
                offsetX={200}
              />
            </>
          )}
        </Layer>
      </Stage>

      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 flex gap-4 z-50">
        <button
          onTouchStart={() => handleMobileControl('left')}
          className="w-12 h-12 bg-blue-500 text-white rounded-full shadow-lg active:bg-blue-600 flex items-center justify-center text-xl font-bold"
        >
          ←
        </button>
        <button
          onTouchStart={() => handleMobileControl('jump')}
          className="w-12 h-12 bg-green-500 text-white rounded-full shadow-lg active:bg-green-600 flex items-center justify-center text-xl font-bold"
        >
          ↑
        </button>
        <button
          onTouchStart={() => handleMobileControl('right')}
          className="w-12 h-12 bg-blue-500 text-white rounded-full shadow-lg active:bg-blue-600 flex items-center justify-center text-xl font-bold"
        >
          →
        </button>
      </div>

      {gameState === 'gameOver' && (
        <button
          onClick={resetGame}
          className="fixed top-1/2 left-1/2 transform -translate-x-1/2 translate-y-16 px-8 py-4 bg-gradient-to-r from-green-400 to-blue-500 text-white text-xl font-bold rounded-2xl shadow-2xl hover:scale-105 transition-transform z-50"
        >
          🔄 Restart Game
        </button>
      )}
    </div>
  );
};

export default DodgeTheRainGame;